import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import { Container } from "@/components/layout/Container";
import { StratoButton } from "@/components/common/StratoButton";
import { NAV_LINKS } from "@/content/homeContent";
import stratoLogo from "@/assets/brand/strato-logo.png";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkClass = ({ isActive }) =>
    cn(
      "whitespace-nowrap text-[13px] font-medium transition-colors duration-150",
      isActive
        ? "text-strato-blue"
        : "text-strato-graphite/70 hover:text-strato-graphite",
    );

  return (
    <header
      className={cn(
        "sticky top-0 z-50 border-b transition-colors duration-200",
        scrolled
          ? "border-strato-gray-100 bg-white/85 backdrop-blur-md"
          : "border-transparent bg-white",
      )}
    >
      <Container className="flex h-16 items-center justify-between gap-4">
        <Link to="/" data-testid="header-wordmark" className="flex items-center gap-2.5">
          <img
            src={stratoLogo}
            alt="Strato Lab 3D"
            className="h-9 w-9 rounded-md object-contain"
          />
          <span className="font-display text-[15px] font-semibold tracking-[-0.01em] text-strato-graphite">
            Strato Lab 3D
          </span>
        </Link>

        <nav data-testid="header-nav" className="hidden lg:contents">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`header-nav-link-${link.to.replace('/', '')}`}
              className={linkClass}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/stato-richiesta"
            data-testid="header-status-link"
            className="hidden items-center gap-1.5 text-[13px] font-medium text-strato-graphite/70 transition-colors hover:text-strato-graphite xl:inline-flex"
          >
            <Activity className="h-4 w-4" />
            Stato richiesta
          </Link>
          <StratoButton
            as={Link}
            to="/preventivo"
            data-testid="header-start-quote-button"
            className="px-4 py-2.5 text-[13px]"
          >
            Avvia il preventivo
          </StratoButton>
        </div>

        <button
          type="button"
          data-testid="header-mobile-menu-button"
          aria-label="Apri il menu"
          onClick={() => setOpen((v) => !v)}
          className="ml-auto grid h-10 w-10 place-items-center rounded-lg border border-strato-gray-200 text-strato-graphite lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </Container>

      {open ? (
        <div
          data-testid="header-mobile-menu"
          className="border-t border-strato-gray-100 bg-white lg:hidden"
        >
          <Container className="flex flex-col gap-1 py-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-2 py-3 text-base font-medium text-strato-graphite/80 transition-colors hover:bg-strato-gray-50"
              >
                {link.label}
              </NavLink>
            ))}
            <NavLink
              to="/stato-richiesta"
              onClick={() => setOpen(false)}
              className="rounded-lg px-2 py-3 text-base font-medium text-strato-graphite/80 transition-colors hover:bg-strato-gray-50"
            >
              Stato richiesta
            </NavLink>
            <StratoButton
              as={Link}
              to="/preventivo"
              onClick={() => setOpen(false)}
              data-testid="header-mobile-start-quote-button"
              className="mt-3 w-full"
            >
              Avvia il preventivo
            </StratoButton>
          </Container>
        </div>
      ) : null}
    </header>
  );
};
